import type { ListWelloPolicyCodeAllDto } from '__generated__/api/unauth';
import { Button, Chip, Input, InputDecorator } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgArrowRoundRight, SvgPeople2 } from '@common/icon';
import { zodResolver } from '@common/utils';
import { CustomError, isEnumValue, z } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { isEqual, uniq } from 'es-toolkit';
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { scroller } from 'react-scroll';

import {
  EXCEPTED_CODE,
  EXCEPTED_HIDDEN_CODE,
  UnauthQueryOptions,
  WELLO_POLICY_CODE_TYPE,
} from '@/api';
import { BottomNavPortal } from '@/app/_components/BottomNavPortal';
import { Image, RegionSelect, Tooltip } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { STORAGE_KEY } from '@/constants';
import type { ReactScrollOptions } from '@/types';

enum HELP_MODAL {
  FAMILY_COUNT = 'familyCount',
  FAMILY_INCOME = 'familyIncome',
}

interface FormValues {
  filterValues: ListWelloPolicyCodeAllDto[];
  familyCount: number;
  familyIncome: number;
  unemploymentDate: string | undefined;
}

interface CustomPolicyFilterFormProps {
  initialValues?: Partial<FormValues>;
  onSubmit?: (data: FormValues) => void;
  isSubmitting?: boolean;
  submitButtonLabel?: string;
  resetButton?: boolean;
  genderFilter?: boolean;
  children?: React.ReactNode;
}

export const PolicyFitFilterForm = ({
  initialValues,
  onSubmit,
  isSubmitting,
  submitButtonLabel,
  resetButton,
  children,
  genderFilter,
}: CustomPolicyFilterFormProps) => {
  const { data } = useSuspenseQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/wello-policy-code/all'](),
    staleTime: Infinity,
    gcTime: Infinity,
    select: ({ context }) => {
      const { contents } = context ?? {};

      if (!contents)
        throw new CustomError({ return_message: '정보가 없습니다.' });

      const boundedMetaData: ListWelloPolicyCodeAllDto[][] = [];

      const genderMetaData: ListWelloPolicyCodeAllDto[] = [];

      const regionMetaData: {
        mainRegion: ListWelloPolicyCodeAllDto[];
        subRegion: ListWelloPolicyCodeAllDto[];
      } = {
        mainRegion: [],
        subRegion: [],
      };

      contents.forEach((content) => {
        const { code } = content;
        let { level } = content;

        if (!code || !level) return;

        const isHidden =
          code.includes('99') && !isEnumValue(EXCEPTED_HIDDEN_CODE, code);

        if (isHidden) return;

        if (code?.includes(EXCEPTED_CODE.GENDER)) {
          if (level === 2) genderMetaData.push(content);

          return;
        }

        if (code.startsWith('C14')) level = level - 1;

        if (code?.includes(EXCEPTED_CODE.REGION)) {
          switch (level) {
            case 2:
              return regionMetaData.mainRegion.push(content);
            case 3:
              return regionMetaData.subRegion.push(content);
            default:
              return;
          }
        }

        if (level === 3 && code.startsWith(EXCEPTED_CODE.UNEMPLOYMENT)) return;

        if (level === 1) return boundedMetaData.push([content]);

        const rootMetaData = boundedMetaData.find(([rootMetaData]) => {
          const rootMetaDataCode = rootMetaData.code;

          return rootMetaDataCode ? code.includes(rootMetaDataCode) : false;
        });

        return rootMetaData?.push(content);
      });

      const boundedCustomPolicyMetaData: ListWelloPolicyCodeAllDto[][] = [];
      const boundedInterestMetaData = boundedMetaData.filter((metaDataList) => {
        const [rootMetaData] = metaDataList;

        if (
          rootMetaData.welloPolicyCodeTypeCd === WELLO_POLICY_CODE_TYPE.INTEREST
        ) {
          return true;
        } else {
          //! 관심 필터는 원래 없어서 여기서 예외처리
          if (!rootMetaData.welloPolicyCodeFilterCd) return false;

          boundedCustomPolicyMetaData.push(metaDataList);

          return false;
        }
      });

      return {
        customPolicyMetaDataList: boundedCustomPolicyMetaData.flat(),
        interestedCategoryMetaDataList: boundedInterestMetaData.flat(),
        genderMetaData,
        regionMetaData,
      };
    },
  });

  const rootMetaDataMap = new Map<string, ListWelloPolicyCodeAllDto>();

  const [selectedMetaDataMap, setSelectedMetaDataMap] = useState(
    new Map<string, ListWelloPolicyCodeAllDto>([]),
  );

  const selectedCodeList = [...selectedMetaDataMap.keys()];

  const selectedRegionCodeList = selectedCodeList.filter((code) =>
    code.includes(EXCEPTED_CODE.REGION),
  );

  let selectedMainRegionCode = '';
  let selectedSubRegionCode = '';

  selectedRegionCodeList.forEach((code) => {
    if (code.split('-').length === 2) {
      selectedMainRegionCode = code;
    } else {
      selectedSubRegionCode = code;
    }
  });

  //! ⚠️ 상태 값으로 관리할 필요가 없는 값
  const subTitleCodeList: string[] = [];
  const prevSubTitleCodeList = useRef(subTitleCodeList);

  const [isClicked, setIsClicked] = useState(false);

  useEffect(() => {
    const newSubTitleCodeList = subTitleCodeList.filter(
      (code) => !prevSubTitleCodeList.current.includes(code),
    );

    const scrollTo = newSubTitleCodeList[newSubTitleCodeList.length - 1];

    //! 애니메이션 후 스크롤
    if (scrollTo) {
      const scrollTimmer = setTimeout(() => {
        if (!isClicked) return;

        scroller.scrollTo(scrollTo, {
          smooth: true,
          offset: -100,
        });
      }, 300);

      return () => {
        clearTimeout(scrollTimmer);
        prevSubTitleCodeList.current = subTitleCodeList;
      };
    }

    return () => {
      prevSubTitleCodeList.current = subTitleCodeList;
    };
  });

  const {
    register,
    getValues,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<Omit<FormValues, 'filterValues'>>({
    resolver: (() => {
      const validateDate = (value: string): boolean => {
        let dateValue = value;
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateValue.match(regex)) {
          dateValue = `${value.substring(0, 4)}-${value.substring(4, 6)}-${value.substring(6, 8)}`;
          if (!dateValue.match(regex)) {
            return false;
          }
        }

        const date = new Date(dateValue);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();

        const parts = dateValue.split('-');
        const yearFromStr = Number.parseInt(parts[0], 10);
        const monthFromStr = Number.parseInt(parts[1], 10);
        const dayFromStr = Number.parseInt(parts[2], 10);

        if (
          year !== yearFromStr ||
          month !== monthFromStr ||
          day !== dayFromStr
        ) {
          return false;
        }

        if (yearFromStr < 1900) {
          return false;
        }

        if (month === 2) {
          if ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) {
            return day <= 29;
          } else {
            return day <= 28;
          }
        } else if ([4, 6, 9, 11].includes(month)) {
          return day <= 30;
        } else {
          return day <= 31;
        }
      };

      const baseSchema = z.object({
        familyCount: z.preprocess(
          (val) => {
            if (val === '' || val === undefined || val === null)
              return undefined;

            const num =
              typeof val === 'string'
                ? Number(val)
                : typeof val === 'number'
                  ? val
                  : undefined;

            return num !== undefined && !isNaN(num) ? num : undefined;
          },
          z
            .number({
              error: '가구원 수를 입력해 주세요.',
            })
            .int('정수만 입력해 주세요.')
            .min(1, '1 이상의 숫자만 입력해 주세요.')
            .max(99, '99 이하의 숫자만 입력해 주세요.'),
        ),
        familyIncome: z.preprocess(
          (val) => {
            if (val === '' || val === undefined || val === null)
              return undefined;

            const num =
              typeof val === 'string'
                ? Number(val)
                : typeof val === 'number'
                  ? val
                  : undefined;

            return num !== undefined && !isNaN(num) ? num : undefined;
          },
          z
            .number({
              error: '가구 월소득을 입력해 주세요.',
            })
            .int('정수만 입력해 주세요.')
            .max(9999, '1억 이하의 숫자만 입력해 주세요.'),
        ),
        unemploymentDate: selectedMetaDataMap.has(EXCEPTED_CODE.UNEMPLOYMENT)
          ? z
              .string()
              .min(1, '실직일을 입력해 주세요')
              .refine(validateDate, '정상적인 날짜 형식이 아닙니다.')
              .refine((val) => {
                const textDate = val.replace(
                  /(\d{4})(\d{2})(\d{2})/,
                  '$1-$2-$3',
                );
                const today = new Date();
                const inputDate = new Date(textDate);
                today.setHours(0, 0, 0, 0);

                return inputDate <= today;
              }, '과거의 날짜만 입력할 수 있습니다.')
          : z.string().optional(),
      });

      return zodResolver(baseSchema);
    })(),
    mode: 'onBlur',
    resetOptions: {
      keepDefaultValues: true,
    },
  });

  const resetAll = useCallback(() => {
    let baseFormValues: Partial<FormValues> | undefined = initialValues;

    if (!baseFormValues) {
      const sessionStorageData = sessionStorage.getItem(
        STORAGE_KEY.POLICY_FILTER,
      );

      if (sessionStorageData) baseFormValues = JSON.parse(sessionStorageData);
    }

    if (baseFormValues) {
      const { filterValues, familyIncome, ...restinitialValues } =
        baseFormValues;

      setSelectedMetaDataMap(
        new Map(
          filterValues?.map((metaData) => {
            if (!metaData.code) throw new Error('metaData.code가 없습니다.');

            return [metaData.code, metaData];
          }),
        ),
      );

      reset({
        ...restinitialValues,
        familyIncome: familyIncome ? familyIncome / 10000 : undefined,
      });
    }
  }, [initialValues, reset]);

  useLayoutEffect(() => resetAll(), [resetAll]);

  const [openedHelpModal, setOpenedHelpModal] = useState<HELP_MODAL>();

  const closeModal = () => setOpenedHelpModal(undefined);

  const applySelectedMap = () =>
    setSelectedMetaDataMap(
      (selectedMetaDataMap) => new Map(selectedMetaDataMap),
    );

  const { toast } = useToast();

  const formId = useId();

  const { customPolicyMetaDataList, genderMetaData } = data;

  return (
    <form
      className="relative pb-96 mobile-tablet:pb-0"
      id={formId}
      onSubmit={handleSubmit(
        ({ familyIncome, familyCount, unemploymentDate = undefined }) => {
          const formValues = {
            familyCount,
            unemploymentDate: unemploymentDate
              ? dayjs(unemploymentDate).format('YYYY-MM-DD')
              : undefined,
            filterValues: [...selectedMetaDataMap.values()],
            familyIncome: familyIncome * 10000,
          };

          if (
            genderFilter &&
            !selectedMetaDataMap.has(`${EXCEPTED_CODE.GENDER}-01`) &&
            !selectedMetaDataMap.has(`${EXCEPTED_CODE.GENDER}-02`)
          ) {
            scroller.scrollTo(EXCEPTED_CODE.GENDER, {
              smooth: true,
              offset: -100,
            } satisfies ReactScrollOptions);

            return toast({
              message: '성별을 선택해주세요.',
              type: 'fail',
            });
          }

          sessionStorage.setItem(
            STORAGE_KEY.POLICY_FILTER,
            JSON.stringify(formValues),
          );

          onSubmit?.(formValues);
        },
        (error) => {
          for (const key in error) {
            const message = error[key as keyof typeof error]?.message;

            if (message) {
              scroller.scrollTo(key, {
                smooth: true,
                offset: -100,
              } satisfies ReactScrollOptions);

              return toast({
                message,
                type: 'fail',
              });
            }
          }
        },
      )}
    >
      <section className="px-20">
        <RegionSelect
          enableLocation={false}
          id={EXCEPTED_CODE.REGION}
          value={{
            mainRegionCode: selectedMainRegionCode,
            subRegionCode: selectedSubRegionCode,
          }}
          onChange={({ mainRegion, subRegion }) => {
            selectedMetaDataMap.forEach(({ code }) => {
              if (code?.startsWith(EXCEPTED_CODE.REGION))
                selectedMetaDataMap.delete(code);
            });

            if (!mainRegion.code || !subRegion.code)
              throw new CustomError({
                return_message: '지역 데이터가 올바르지 않습니다.',
              });

            selectedMetaDataMap.set(mainRegion.code, mainRegion);
            selectedMetaDataMap.set(subRegion.code, subRegion);

            applySelectedMap();
          }}
        />
        <p className="mt-10 font-400 typo-caption1">
          지역 설정은{' '}
          <span className="text-B-500">{`맞춤정책 > 맞춤정보 수정 > 지역`}</span>
          에서 언제든지 수정할 수 있어요
        </p>
      </section>
      <dl className="m-0 flex flex-wrap items-start justify-start gap-6 px-20">
        {genderFilter ? (
          <>
            <dt
              className="mb-4 mt-26 flex w-full items-center text-Gray-800 typo-body4"
              id={EXCEPTED_CODE.GENDER}
            >
              성별
            </dt>
            <dd className="flex w-full gap-8">
              {genderMetaData.map((meta, index) => {
                const { code, value } = meta;

                if (!code) return null;

                return (
                  <Button
                    key={index}
                    className="flex-1"
                    dimensions="H48"
                    theme={
                      selectedMetaDataMap.has(code)
                        ? 'primary-fill'
                        : 'sub-line'
                    }
                    onClick={() => {
                      const checked = !selectedMetaDataMap.has(code);

                      if (checked) {
                        selectedCodeList
                          .filter((code) => code.includes(EXCEPTED_CODE.GENDER))
                          .forEach((code) => selectedMetaDataMap.delete(code));

                        selectedMetaDataMap.set(code, meta);
                        applySelectedMap();
                      } else {
                        selectedMetaDataMap.delete(code);
                        applySelectedMap();
                      }
                    }}
                  >
                    {value}
                  </Button>
                );
              })}
            </dd>
          </>
        ) : null}
        {customPolicyMetaDataList?.map((metaData, index) => {
          const {
            code,
            value,
            level,
            duplicateAvailYN: duplicate_avail_yn,
            parentCode: parent_code,
          } = metaData;

          if (!code) return null;

          switch (level) {
            case 1: {
              rootMetaDataMap.set(code, metaData);

              const defaultCase = (
                <dt
                  className="mb-4 mt-26 flex w-full items-center gap-4 text-Gray-800 typo-body4"
                  id={code}
                >
                  {metaData.imageUrl ? (
                    <Image
                      alt=""
                      height={18}
                      src={metaData.imageUrl}
                      width={18}
                    />
                  ) : null}
                  {value}
                  {duplicate_avail_yn ? (
                    <span className="text-Gray-600">(중복선택 가능)</span>
                  ) : null}
                </dt>
              );

              switch (code) {
                case 'C05':
                  return (
                    <Fragment key={index}>
                      {selectedCodeList.includes(EXCEPTED_CODE.UNEMPLOYMENT) ? (
                        <>
                          <dt className="mb-4 mt-26 flex w-full animate-fade-up items-center gap-4 text-Gray-800 duration-300 typo-body4">
                            실직일
                            <Tooltip>
                              <Tooltip.Area>
                                <div className="flex size-16 items-center justify-center rounded-pill border-1 border-Gray-500 font-700 text-Gray-700 typo-caption1">
                                  ?
                                </div>
                              </Tooltip.Area>
                              <Tooltip.Content align="start">
                                4대보험 기준 실직 시작일
                                <br />
                                취업 경험이 없는 경우 성년(만19세)이 된 시점
                              </Tooltip.Content>
                            </Tooltip>
                          </dt>
                          <dd className="w-full animate-fade-up duration-300">
                            <InputDecorator
                              validationMessage={
                                errors.unemploymentDate?.message
                              }
                            >
                              <Input
                                {...register('unemploymentDate')}
                                placeholder="실직일자를 입력해주세요(YYYYMMDD)"
                                type="text-date"
                              />
                            </InputDecorator>
                          </dd>
                        </>
                      ) : null}
                      {defaultCase}
                    </Fragment>
                  );
                case 'C06':
                  return (
                    <Fragment key={index}>
                      <dt className="mb-4 mt-26 flex w-full items-center text-Gray-800 typo-body4">
                        가구원 수
                      </dt>
                      <dd className="w-full">
                        <InputDecorator
                          validationMessage={errors.familyCount?.message}
                        >
                          <Input
                            {...register('familyCount')}
                            max={99}
                            placeholder="가구원 수를 입력해 주세요"
                            rightContent="명"
                            type="number"
                          />
                        </InputDecorator>
                        <button
                          className="mt-4 flex items-center gap-4 border-none text-Gray-600 typo-caption1"
                          type="button"
                          onClick={() =>
                            setOpenedHelpModal(HELP_MODAL.FAMILY_COUNT)
                          }
                        >
                          <div className="flex size-16 items-center justify-center rounded-pill border-1 border-Gray-500 font-700 text-Gray-700">
                            ?
                          </div>
                          가구원수 기준을 모르겠어요.
                        </button>
                      </dd>
                      <dt className="mb-4 mt-26 flex w-full items-center text-Gray-800 typo-body4">
                        가구 월소득
                      </dt>
                      <dd className="w-full">
                        <InputDecorator
                          validationMessage={errors.familyIncome?.message}
                        >
                          <Input
                            {...register('familyIncome')}
                            maxLength={4}
                            placeholder="가구 월소득을 입력해 주세요"
                            rightContent="만원"
                            type="local-string-number"
                          />
                        </InputDecorator>
                        <button
                          className="mt-4 flex items-center gap-4 border-none text-Gray-600 typo-caption1"
                          type="button"
                          onClick={() =>
                            setOpenedHelpModal(HELP_MODAL.FAMILY_INCOME)
                          }
                        >
                          <div className="flex size-16 items-center justify-center rounded-pill border-1 border-Gray-500 font-700 text-Gray-700 typo-caption1">
                            ?
                          </div>
                          가구 월소득은 가구원의 월소득을 모두 합산한
                          금액입니다.
                        </button>
                      </dd>
                      <ResponsiveDialog
                        opened={openedHelpModal === HELP_MODAL.FAMILY_COUNT}
                        onClose={closeModal}
                        drawerType="bottom"
                      >
                        <div className="flex flex-col items-center gap-10 pb-16">
                          <SvgPeople2 className="size-42 text-B-500" />
                          <h4 className="font-600 typo-body1">가구원 수란?</h4>
                          <section className="word-break-keep text-center typo-body3">
                            한 가구에서 함께 살고 있는 구성원 수를 계산합니다.
                            <br />
                            주민등록 여부와 관계 없이 같이 살고 있을 경우 <br />
                            (예 : 친구)에도 포함됩니다.
                            <br />
                            <span className="font-600 text-B-500">
                              1인 가구의 경우 &apos;1명&apos;
                            </span>
                            으로 작성해주세요!
                          </section>
                        </div>
                      </ResponsiveDialog>
                      <ResponsiveDialog
                        opened={openedHelpModal === HELP_MODAL.FAMILY_INCOME}
                        onClose={closeModal}
                        drawerType="bottom"
                      >
                        <div className="flex flex-col items-center gap-10 pb-16">
                          <SvgPeople2 className="size-42 text-B-500" />
                          <h4 className="font-600 typo-body1">
                            가구 월소득이란?
                          </h4>
                          <section className="word-break-keep text-center typo-body3">
                            가구 모든 구성원의 근로소득, 건강보험료 납입고지서
                            등으로 확인되는 최근 6개월 간의 평균소득을
                            의미합니다.
                          </section>
                          <section className="word-break-keep text-center typo-body3">
                            가구의 월 소득을 정확하게 입력하기 어려우시다면
                          </section>
                          <section className="word-break-keep text-center typo-body3">
                            <span className="font-600 text-B-500">
                              [본인소득 + (200만원 X 본인을 제외한 소득이 있는
                              가구원 수)]
                            </span>
                            를 입력해 주세요
                          </section>
                          <p className="typo-caption1">
                            · 200만원은 2023년 최저임금 기준입니다.
                          </p>
                        </div>
                      </ResponsiveDialog>
                      {defaultCase}
                    </Fragment>
                  );

                default:
                  return <Fragment key={index}>{defaultCase}</Fragment>;
              }
            }

            case 2: {
              if (!parent_code) return null;

              const rootMetaData = rootMetaDataMap.get(parent_code);

              if (!rootMetaData) return null;

              const isDuplicatable = rootMetaData.duplicateAvailYN;

              return (
                <dd key={index} className="m-0 w-fit">
                  <Chip
                    checked={selectedMetaDataMap.has(code)}
                    className="px-10 py-5 typo-body4"
                    onCheckedChange={(checked) => {
                      //* 최신 선택된 코드 리스트를 가져오기 위한 함수
                      const getSelectedCodeList = () => [
                        ...selectedMetaDataMap.keys(),
                      ];

                      if (checked) {
                        if (isDuplicatable) {
                          selectedMetaDataMap.set(code, metaData);
                        } else {
                          const duplicatedCode = getSelectedCodeList().filter(
                            (code) => code.includes(parent_code),
                          );

                          duplicatedCode.forEach((code) => {
                            selectedMetaDataMap.delete(code);
                          });

                          selectedMetaDataMap.set(code, metaData);
                        }
                      } else {
                        getSelectedCodeList().forEach((selectedCode) => {
                          if (selectedCode.includes(code))
                            selectedMetaDataMap.delete(selectedCode);
                        });
                      }

                      //* 해당사항 없음에 대한 예외처리
                      if (code.includes(EXCEPTED_CODE.TARGET_CHARACTERISTICS)) {
                        const isTargetEmpty =
                          code ===
                          EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY;

                        if (checked) {
                          //* 해당 사항 없음 선택시 다른 선택된 대상 특성 삭제
                          if (isTargetEmpty) {
                            getSelectedCodeList().forEach((code) => {
                              if (
                                code.includes(
                                  EXCEPTED_CODE.TARGET_CHARACTERISTICS,
                                ) &&
                                code !==
                                  EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY
                              )
                                selectedMetaDataMap.delete(code);
                            });
                          } //* 다른 대상 특성 선택시 해당 사항 없음 삭제
                          else {
                            selectedMetaDataMap.delete(
                              EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY,
                            );
                          }
                        } //* 대상 특성에 아무것도 선택하지 않았을 때 해당 사항 없음 추가
                        else {
                          const hasSelectedTargetCharacteristics =
                            getSelectedCodeList().some((code) =>
                              code.includes(
                                EXCEPTED_CODE.TARGET_CHARACTERISTICS,
                              ),
                            );

                          if (!hasSelectedTargetCharacteristics) {
                            const targetCharacteristicsEmptyMetaData =
                              customPolicyMetaDataList.find(
                                ({ code }) =>
                                  code ===
                                  EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY,
                              );

                            if (!targetCharacteristicsEmptyMetaData)
                              throw new Error(
                                'meta data에 대상 특성 해당 없음이 없습니다.',
                              );

                            selectedMetaDataMap.set(
                              EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY,
                              targetCharacteristicsEmptyMetaData,
                            );
                          }
                        }
                      }

                      applySelectedMap();
                    }}
                    onClick={() => {
                      setIsClicked(true);
                    }}
                  >
                    {value}
                  </Chip>
                </dd>
              );
            }

            default: {
              if (!parent_code) return null;

              const hasSelectedParentMetaData =
                selectedMetaDataMap.has(parent_code);

              if (!hasSelectedParentMetaData) return null;

              //! ⚠️ 서버에서 받아온 선택했던 값들은 메타 데이터 값의 정보들이 완벽하지 않음 (ex. duplicate_avail_yn가 모두 false로 나오는 등)
              const parentMetaData = selectedMetaDataMap.get(parent_code);

              if (!parentMetaData) return null;

              const isFirstChild = !subTitleCodeList.includes(parent_code);

              const isDuplicatable = parentMetaData.duplicateAvailYN;

              if (isFirstChild) {
                subTitleCodeList.push(parent_code);
              }

              return (
                <Fragment key={index}>
                  {isFirstChild ? (
                    <dt
                      className="mb-4 mt-26 w-full animate-fade-up text-Gray-800 duration-300 typo-body4"
                      id={parent_code}
                    >
                      {parentMetaData.value}
                      {isDuplicatable ? <span>(중복선택 가능)</span> : null}
                    </dt>
                  ) : null}
                  <dd className="m-0 w-fit animate-fade-up duration-300">
                    <Chip
                      checked={selectedMetaDataMap.has(code)}
                      className="px-10 py-5 typo-body4"
                      onChange={(e) => {
                        const { checked } = e.target;

                        if (checked) {
                          if (isDuplicatable) {
                            selectedMetaDataMap.set(code, metaData);
                          } else {
                            const duplicatedCode = selectedCodeList.filter(
                              (code) =>
                                code !== parent_code &&
                                code.includes(parent_code),
                            );

                            duplicatedCode.forEach((code) => {
                              selectedMetaDataMap.delete(code);
                            });

                            selectedMetaDataMap.set(code, metaData);
                          }
                        } else {
                          selectedCodeList.forEach((selectedCode) => {
                            if (selectedCode.includes(code))
                              selectedMetaDataMap.delete(selectedCode);
                          });
                        }

                        applySelectedMap();
                      }}
                    >
                      {value}
                    </Chip>
                  </dd>
                </Fragment>
              );
            }
          }
        })}
      </dl>
      <BottomNavPortal hasShadow={false}>
        <footer className="flex w-full gap-8 p-20">
          {children}
          {resetButton ? (
            <Button
              theme="sub-text"
              onClick={() => {
                const NewInitialValues = {
                  ...initialValues,
                  familyIncome: initialValues?.familyIncome
                    ? initialValues?.familyIncome / 10000
                    : undefined,
                };

                const currentValue = {
                  ...getValues(),
                  filterValues: [...selectedMetaDataMap.values()],
                };

                if (isEqual(NewInitialValues, currentValue)) {
                  toast({
                    message: '변경사항이 없어요',
                  });

                  return;
                }
                resetAll();
                toast({
                  message: '초기화되었습니다.',
                });
              }}
            >
              <SvgArrowRoundRight className="size-[1em]" />
              초기화
            </Button>
          ) : null}
          <Button
            className="flex-1"
            disabled={(() => {
              if (isSubmitting) return true;

              if (!selectedMainRegionCode || !selectedSubRegionCode)
                return true;

              const selectedParentCodeOfCodeList: string[] = [];

              selectedMetaDataMap.forEach((metaData) => {
                const { parentCode: parent_code } = metaData;
                if (parent_code) selectedParentCodeOfCodeList.push(parent_code);
              });

              //* 선택안한 2차 메타데이터 확인
              for (const [rootMetaCode] of rootMetaDataMap.entries())
                if (!selectedParentCodeOfCodeList.includes(rootMetaCode))
                  return true;

              //* 선택안한 3차 이상 메타데이터 확인
              const tempHasChildrenSubMetaCodeList: string[] = [];

              customPolicyMetaDataList?.forEach(
                ({ level, parentCode: parent_code }) => {
                  if (2 < (level ?? 0) && parent_code)
                    tempHasChildrenSubMetaCodeList.push(parent_code);
                },
              );

              const hasChildrenSubMetaCodeList = uniq(
                tempHasChildrenSubMetaCodeList,
              );

              for (const [code, metaCode] of selectedMetaDataMap.entries()) {
                if ((metaCode.level ?? 0) < 2) continue;

                const hasChildren = hasChildrenSubMetaCodeList.includes(code);

                if (!hasChildren) continue;

                const isChildrenSelected =
                  selectedParentCodeOfCodeList.includes(code);

                if (!isChildrenSelected) return true;
              }

              return false;
            })()}
            form={formId}
            loading={isSubmitting}
            type="submit"
          >
            {submitButtonLabel}
          </Button>
        </footer>
      </BottomNavPortal>
    </form>
  );
};
